import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Info, ChevronRight, Users, BookOpen, Star, User, List, Calendar, DollarSign, Search, Award, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

interface StepItem {
  step: number;
  title: string;
  description: string;
  icon: React.ReactNode;
}

export const HowItWorksSection: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'jogador' | 'mentor'>('jogador');
  
  const stepsJogador: StepItem[] = [
    {
      step: 1,
      title: "Escolha seu Mentor",
      description: "Navegue pelos perfis de mentores e encontre o especialista ideal para o seu jogo favorito.",
      icon: <Users className="h-12 w-12 text-[#00B6F0]" />
    },
    {
      step: 2,
      title: "Agende sua Mentoria",
      description: "Escolha o formato de mentoria que prefere e selecione um horário disponível na agenda do mentor.",
      icon: <BookOpen className="h-12 w-12 text-[#00B6F0]" />
    },
    {
      step: 3,
      title: "Evolua seu Gameplay",
      description: "Participe da sessão, aprenda com profissionais e eleve seu nível de jogo com feedback personalizado.",
      icon: <Star className="h-12 w-12 text-[#00B6F0]" />
    }
  ];

  const stepsMentor: StepItem[] = [
    {
      step: 1,
      title: "Crie seu Perfil",
      description: "Registre-se facilmente e construa seu perfil profissional, incluindo suas redes sociais e agendamento de horários.",
      icon: <User className="h-12 w-12 text-[#00B6F0]" />
    },
    {
      step: 2,
      title: "Divulgue suas Mentorias",
      description: "Cadastre suas opções de mentoria, definindo jogo, duração, modalidade e preço para atrair jogadores.",
      icon: <List className="h-12 w-12 text-[#00B6F0]" />
    },
    {
      step: 3,
      title: "Gerencie e Ganhe",
      description: "Receba notificações sobre sessões agendadas, conduza mentorias e ganhe dinheiro ajudando outros jogadores.",
      icon: <DollarSign className="h-12 w-12 text-[#00B6F0]" />
    }
  ];

  const currentSteps = activeTab === 'jogador' ? stepsJogador : stepsMentor;
  
  return (
    <section id="como-funciona-section" className="py-12 md:py-16 px-4 bg-[#0c1117] relative">
      <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-center opacity-10"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <Info className="h-6 w-6 text-[#00B6F0]" />
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center">Como Funciona</h2>
        </div>

        {/* Botões de alternância */}
        <div className="flex justify-center mb-12">
          <div className="flex bg-[#181D24] rounded-lg p-1 border border-[#223342]">
            <button
              onClick={() => setActiveTab('jogador')}
              className={`px-6 py-3 rounded-md font-semibold transition-all duration-200 ${
                activeTab === 'jogador'
                  ? 'bg-[#00B6F0] text-black'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Como Jogador
            </button>
            <button
              onClick={() => setActiveTab('mentor')}
              className={`px-6 py-3 rounded-md font-semibold transition-all duration-200 ${
                activeTab === 'mentor'
                  ? 'bg-[#00B6F0] text-black'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Como Mentor
            </button>
          </div>
        </div>

        {/* Steps Grid */}
        <motion.div 
          key={activeTab}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {currentSteps.map((item, index) => (
            <motion.div 
              key={index} 
              className="bg-[#181D24] border border-[#223342] rounded-xl p-8 hover:shadow-[0_0_20px_rgba(0,182,240,0.2)] transition-all hover:-translate-y-1" 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 + 0.2 }}
            >
              <div className="w-16 h-16 rounded-full bg-[#1A2233] flex items-center justify-center mb-6">
                {item.icon}
              </div>
              <div className="text-[#00B6F0] font-bold text-lg mb-2">Passo {item.step}</div>
              <h3 className="text-white text-xl font-bold mb-3">{item.title}</h3>
              <p className="text-gray-400">{item.description}</p>
            </motion.div>
          ))}
        </motion.div>
        
        <div className="mt-12 text-center">
          <Button 
            onClick={() => navigate("/como-funciona")} 
            variant="outline" 
            size="lg" 
            className="border-[#00B6F0] text-[#00B6F0] hover:bg-[#00B6F0]/10"
          >
            Saiba Mais
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};
